/**
 * Notifications — the one email intake sends, and everything about it a
 * Company decides.
 *
 * A Company receives exactly one kind of email from HireOS: the drafted
 * job, the moment a Customer's call or form submission has been priced.
 * So there is no catalogue to browse here as there is in the RMS's
 * studio, and no drill-down either: two groups, whether it is sent and
 * who it reaches, on one screen.
 *
 * The address it goes to is the first row of that second group. It was
 * in General, on a screen about who the Company is, while the switch
 * that stops the email and the list of everyone else copied on it were
 * here — so "who is told about a new job" was answered in two places,
 * and the one address that always gets it was the one not on this
 * screen. It is still `companies.contact_email`, written through the
 * shell's company-settings fetch as it always was; what moved is where
 * it is read.
 *
 * The rendered email itself is not on it. It was, with the opening and
 * closing notes edited in place on the email — a whole rendered email
 * dwarfing the two rows above it, to write two sentences almost nobody
 * had written — and it is what made this a place with rooms in it.
 *
 * Turning the email off stops the notification and nothing else. The job
 * still drafts into the rental system exactly as it did — the send is
 * downstream of the draft by construction, and nothing on this screen can
 * reach the draft.
 */
import { useState } from "react";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { InlineBanner } from "../../components/ui/InlineBanner";
import {
  TextSettingRow,
  ToggleSettingRow,
} from "../../components/ui/SettingRow";
import { TrashIcon } from "../../components/ui/icons";
import {
  getInquiryEmail,
  saveInquiryEmail,
  type CompanySettings,
  type CompanySettingsUpdate,
  type InquiryEmail,
  type InquiryEmailUpdate,
} from "../../lib/settings-api";
import { useAction } from "../../lib/use-action";
import { useSettingsSource } from "../../lib/use-settings-source";
import {
  FieldLabel,
  Loading,
  SectionHeader,
} from "./section-chrome";
import styles from "./InquiryEmailSection.module.css";

export function InquiryEmailSection({
  settings,
  onPatch,
}: {
  settings: CompanySettings;
  onPatch: (patch: CompanySettingsUpdate) => Promise<void>;
}) {
  const {
    data: email,
    setData: setEmail,
    loadError,
  } = useSettingsSource(getInquiryEmail);

  /** Partial PUT; the answer is the whole section, notes included. */
  const patch = async (fields: InquiryEmailUpdate) => {
    setEmail(await saveInquiryEmail(fields));
  };

  return (
    <section>
      <SectionHeader
        title="Notifications"
        description="What you get when a new job has been priced."
      />

      {loadError && (
        <Banner variant="error" className={styles.notice}>
          {loadError}
        </Banner>
      )}

      {!email ? (
        <Loading />
      ) : (
        <div>
          <ToggleSettingRow
            id="inquiry-email-enabled"
            title="Email me new enquiries"
            description="One email per job."
            checked={email.enabled}
            onChange={(enabled) => patch({ enabled })}
          />
          {/* The address the email is actually sent to comes before the
              list: the list is "who else", which is not a question a
              reader can answer without seeing who. */}
          <TextSettingRow
            id="company-contact-email"
            title="Notification email"
            description="Where new enquiries are emailed."
            type="email"
            value={settings.contact_email}
            required
            onSave={(contact_email) => onPatch({ contact_email })}
          />
          <ExtraRecipients email={email} onPatch={patch} />
        </div>
      )}
    </section>
  );
}

/**
 * The extra addresses, as a list rather than one comma-separated field:
 * an address is added or removed one at a time, and a typo in one is not
 * a reason to refuse the other four.
 */
function ExtraRecipients({
  email,
  onPatch,
}: {
  email: InquiryEmail;
  onPatch: (fields: InquiryEmailUpdate) => Promise<void>;
}) {
  const [adding, setAdding] = useState("");
  const { working: busy, error, run } = useAction();

  const write = (extra_recipients: string[]) =>
    run(() => onPatch({ extra_recipients }));

  const add = async () => {
    const next = adding.trim();
    if (next === "") return;
    if (await write([...email.extra_recipients, next])) setAdding("");
  };

  return (
    <>
      {email.extra_recipients.length > 0 && (
        <ul className={styles.recipients}>
          {email.extra_recipients.map((address) => (
            <li
              key={address}
              className={styles.recipient}
            >
              <span className={styles.address}>
                {address}
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                aria-label={`Remove ${address}`}
                onClick={() =>
                  void write(
                    email.extra_recipients.filter((a) => a !== address),
                  )
                }
              >
                <TrashIcon className={styles.icon} />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <form
        className={styles.addForm}
        onSubmit={(e) => {
          e.preventDefault();
          void add();
        }}
      >
        <FieldLabel htmlFor="inquiry-email-add-recipient">
          Add an email address
        </FieldLabel>
        <div className={styles.addRow}>
          <input
            id="inquiry-email-add-recipient"
            type="email"
            autoComplete="off"
            className={styles.addInput}
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
          />
          <Button
            size="sm"
            type="submit"
            loading={busy}
            disabled={adding.trim() === ""}
          >
            Add
          </Button>
        </div>
        {error && <InlineBanner variant="error">{error}</InlineBanner>}
      </form>
    </>
  );
}
